/**
 * @file Authenticates the current user against Identity and caches the resulting JWT.
 */
import { wait } from "@api/helpers/wait";
import { HOST } from "@components/Constants";
import { ACCESS_DENIED_ERROR } from "@api/errors";

import Auth from "./Auth.js";
import Storage from "./Storage.js";
import { jsonRequest } from "../request/index.js";

const AUTHENTICATE_DEV_URL = `${HOST}/api/identity/Authenticate`;

const LABEL_SERVICE_FAILED = `The sign-in service did not answer. Reload the
page to try again.`;

type OnAuthenticationError = (message: string) => void;

/**
 * Returns a cached authentication JWT when one is valid, or signs the user in and caches the new one.
 */
const authenticate = async (
  onAuthenticationError: OnAuthenticationError,
  isBackstage: boolean,
  shouldRefresh = false,
): Promise<string | null> => {
  if (!isBackstage) {
    const impersonatedAuthentication = Storage.popImpersonatedAuthentication();

    if (impersonatedAuthentication) {
      Storage.setInvestorAuthentication(impersonatedAuthentication);

      // eslint-disable-next-line no-console
      console.info("Authenticated as impersonated user");

      return impersonatedAuthentication;
    }
  }

  if (!shouldRefresh) {
    const authentication = isBackstage
      ? Storage.backstageAuthentication
      : Storage.investorAuthentication;

    if (authentication) {
      return authentication;
    }
  }

  // NOTE: Wait for 10ms to allow one of the threads to start.
  await wait(10);

  const { isPendingAuthentication } = Storage;

  if (isPendingAuthentication) {
    do {
      await wait(250);

    } while (Storage.isPendingAuthentication);

    const authenticationJwt = Storage.getAuthentication(isBackstage);
    return authenticationJwt;
  }

  Storage.startAuthentication();

  // The threads above wait on the pending flag, therefore every exit of this
  // block must clear it. A leaked flag makes those threads wait forever.
  try {
    const idToken = await Auth.getIdToken();
    const hasNoAuthenticationParameter = !idToken;

    if (hasNoAuthenticationParameter) {
      return null;
    }

    const redirectUrl = isBackstage
      ? `${HOST}/backstage/`
      : `${HOST}/`;

    const mutation = {
      idToken,
      redirectUrl,
    };

    let authenticationJwt: string;

    try {
      const { data } = await jsonRequest(AUTHENTICATE_DEV_URL, { mutation });
      authenticationJwt = data.authenticationJwt;

    } catch (error) {
      // The backend writes a message for a person on a refusal only. Each
      // other failure carries an internal string, therefore the screen shows
      // the curated text for it.
      const refusalMessage = error.originalError?.message;

      const isAccessDenied = error.code === ACCESS_DENIED_ERROR;
      const hasRefusalMessage = !!refusalMessage;

      const isRefusal =
        isAccessDenied &&
        hasRefusalMessage;

      const errorMessage = isRefusal
        ? refusalMessage
        : LABEL_SERVICE_FAILED;

      onAuthenticationError(errorMessage);

      return null;

    }

    const setAuthentication = isBackstage
      ? Storage.setBackstageAuthentication
      : Storage.setInvestorAuthentication;

    setAuthentication(authenticationJwt);

    return authenticationJwt;

  } finally {
    Storage.stopAuthentication();

  }
};

export default authenticate;
